import React, { useContext, useState, useEffect } from "react";
import { Table } from "react-bootstrap";
import { withTranslation, useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { toast } from "react-toastify";
import { LoaderContext } from "../../../../App";
import StandardService from "../../../../core/service/StandardService";
import SubStdService from "../../../../core/service/SubStdService";
import Utils from "../../../../core/utilities/Utils";
import '../DataView.scss'
import { LS_REGULATION_SELECTED_KEY, LS_COMP_FILTER_KEY, DataViewCategoryType, CompareCategoryType, CategoryType, LS_COMPANY_INFO_KEY } from "../../../../core/constants/common";
import CompareFilter from "./CompareFilter";
import CompareHeader from "./CompareHeader";
import QuestionService from "../../../../core/service/QuestionService";
import {
	getCompanyInfoFromStorage,
	getYearDropdownLabel,
	isFinancialYearReporting,
} from "../../../../core/utilities/assessmentPeriodUtils";

const CompareDataView = ({ t }) => {

	const glbRegulationId = localStorage.getItem(LS_REGULATION_SELECTED_KEY);
	const { setLoaderDisplay } = useContext(LoaderContext);

	const companyInfo = (() => {
		try {
			return JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
		} catch {
			return {};
		}
	})();
	const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
	const isFinancialYear = dataFreqType === 'FY';

	const toShortMonth = (monthStr) => {
		if (!monthStr) return '';
		return monthStr.charAt(0).toUpperCase() + monthStr.slice(1, 3).toLowerCase();
	};

	const getFYLabel = (yearStr) => {
		const y = Number(yearStr);
		const fyStart = companyInfo?.financialYearStart ?? 'APRIL';
		const fyEnd = companyInfo?.financialYearEnd ?? 'MARCH';
		return `${toShortMonth(fyStart)} ${y - 1} - ${toShortMonth(fyEnd)} ${y}`;
	};
	const compFilter = JSON.parse(localStorage.getItem(LS_COMP_FILTER_KEY));
	const [filter, setFilter] = useState({
		standardId: compFilter?.glbRegulationId ?? glbRegulationId,
		periodType: compFilter?.periodType ?? '',
		period: compFilter?.period ?? null,
		category: compFilter?.category ?? 'ENVIRONMENTAL',
		categoryType: compFilter?.categoryType ?? DataViewCategoryType.ENVIRONMENTAL,
		topicIds: compFilter?.topicIds ?? [],
		parentBranchIds: compFilter?.parentBranchIds ?? [],
		childBranchIds: compFilter?.childBranchIds ?? [],
		businessUnitIds: compFilter?.businessUnitIds ?? [],
		childEntities: compFilter?.childEntities ?? [],
		parentEntities: compFilter?.parentEntities ?? [],
		selectedRepPeriod: compFilter?.selectedRepPeriod ?? [],
	});
	const [dataViewData, setDataViewData] = useState([]);
	const [dataPoints, setDataPoints] = useState({});
	const [topicVisibility, setTopicVisibility] = useState({});
	const [dpVisibility, setDPVisibility] = useState({});
	const [yearData, setYearData] = useState([]);
	const [branches, setBranches] = useState([]);
	const [topics, setTopics] = useState([]);
	const [showFilter, setShowFilter] = useState(false);
	let sortOrder = [];
	const [colHideShow, setColHideShow] = useState({
		codes: true,
		uom: true
	});
	const { k, i18n } = useTranslation();

	const preLoadData = () => {
		const currentYear = new Date().getFullYear();
		const previousYear = currentYear - 1;
		const preloadCompanyInfo = getCompanyInfoFromStorage();
		const preloadIsFinancialYear = isFinancialYearReporting(preloadCompanyInfo);
		const compareFilter = {
			standardId: glbRegulationId,
			periodType: 'YEARLY',
			period: { [currentYear]: [], [previousYear]: [] },
			category: 'ENVIRONMENTAL',
			topicIds: [],
			parentBranchIds: [],
			childBranchIds: [],
			childEntities: [],
			parentEntities: [],
			selectedRepPeriod: [
				{ id: 0, value: currentYear, displayValue: getYearDropdownLabel(currentYear, preloadIsFinancialYear), category: "Years" },
				{ id: 1, value: previousYear, displayValue: getYearDropdownLabel(previousYear, preloadIsFinancialYear), category: "Years" }
			]
		}
		getDataViewData(compareFilter);
		setFilter(compareFilter);
	}

	useEffect(() => {
		if (compFilter) {
			const compareFilter = {
				standardId: compFilter.standardId,
				periodType: compFilter.periodType,
				period: compFilter.period,
				category: CompareCategoryType[compFilter.categoryType],
				topicIds: compFilter.topicIds,
				parentBranchIds: compFilter.parentEntities,
				childBranchIds: compFilter.childEntities,
				businessUnitIds: compFilter.businessUnitIds,
				childEntities: compFilter?.childEntities,
				parentEntities: compFilter?.parentEntities,
				selectedRepPeriod: compFilter?.selectedRepPeriod,
			}
			getDataViewData(compareFilter);
			setFilter(compareFilter);
		} else {
			preLoadData();
		}
	}, []);

	const getDataViewData = (f) => {
		setLoaderDisplay(true);
		StandardService.getMetricComparison(f)
			.then(res => {
				setLoaderDisplay(false);
				if (res.status !== 200) return;
				setDataViewData(res.data.data);
				processFilteredData(res.data.data);
				setDataPoints({});
			}).catch(err => {
				setLoaderDisplay(false);
			});
	};

	const getDataPoints = (metricId) => {
		const filterToUse = filter;
		filterToUse['metricId'] = metricId;
		sortOrder = filterToUse.businessUnitIds;
		setLoaderDisplay(true);
		SubStdService.getBranchBasedComparisonData(filterToUse).then((res) => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			let dataPoints = [];
			let brConsolidated2nd = [];
			let brConsolidated3rd = [];
			let count = 0;
			if (res.data.data) {
				for (const [parentKey, patentValue] of Object.entries(res.data.data)) {
					if (patentValue) {
						for (const [childKey, childValue] of Object.entries(patentValue)) {
							if (childValue.length > 0) {
								if (count == 0) {
									childValue.forEach(element => {
										if (element['branchConsolidated'].length > 0) {
											element['branchConsolidated'].sort((a, b) => {
												return sortOrder.indexOf(a.branchId) - sortOrder.indexOf(b.branchId);
											});
											let value = { branchName: t("Total.label"), value: element.total, uom: element.uom }
											element['branchConsolidated'].unshift(value);
										} else {
											let value = { branchName: t("Total.label"), value: element.total, uom: element.uom }
											element['branchConsolidated'] = [value]
										}
									});
									dataPoints = childValue;
								}
								if (count > 0) {
									childValue.forEach((element, key1) => {
										if (element['branchConsolidated'].length > 0) {
											element['branchConsolidated'].sort((a, b) => {
												return sortOrder.indexOf(a.branchId) - sortOrder.indexOf(b.branchId);
											});
											// if (count == 1) {
											// 	let value = { branchName: t("Total.label"), value: element.total, uom: element.uom }
											// 	element['branchConsolidated'].unshift(value);
											// 	brConsolidated2nd.push(element['branchConsolidated']);
											// }
											// else if (count == 2) {
											// 	let value = { branchName: t("Total.label"), value: element.total, uom: element.uom }
											// 	element['branchConsolidated'].unshift(value);
											// 	brConsolidated3rd.push(element['branchConsolidated']);
											// }
											let value = { branchName: t("Total.label"), value: element.total, uom: element.uom }
											element['branchConsolidated'].unshift(value);
											let exBrData = dataPoints[key1]['branchConsolidated'];
											let newBrData = element['branchConsolidated'];
											let mergedData = exBrData.concat(newBrData)
											dataPoints[key1]['branchConsolidated'] = mergedData;
										} else {
											// if (count == 1) {
											// 	let value = { branchName: t("Total.label"), value: element.total, uom: element.uom }
											// 	element['branchConsolidated'] = [value];
											// 	brConsolidated2nd.push(element.branchConsolidated);
											// } else if (count == 2) {
											// 	let value = { branchName: t("Total.label"), value: element.total, uom: element.uom }
											// 	element['branchConsolidated'] = [value];
											// 	brConsolidated3rd.push(element.branchConsolidated);
											// }
											let value = { branchName: t("Total.label"), value: element.total, uom: element.uom }
											element['branchConsolidated'] = [value];
											let exBrData = dataPoints[key1]['branchConsolidated'];
											let newBrData = element['branchConsolidated'];
											let mergedData = exBrData.concat(newBrData)
											dataPoints[key1]['branchConsolidated'] = mergedData;
										}
									});
								}
								count++
							}
						}
					}
				}
				// dataPoints.forEach((element, key) => {
				// 	if (brConsolidated2nd.length > 0) {
				// 		element['branchConsolidated'] = element['branchConsolidated'].concat(brConsolidated2nd[key]);
				// 	}
				// 	if (brConsolidated3rd.length > 0) {
				// 		element['branchConsolidated'] = element['branchConsolidated'].concat(brConsolidated3rd[key]);
				// 	}
				// })
			}

			setDataPoints(existingDPs => {
				return {
					...existingDPs,
					[metricId]: dataPoints
				};
			});
			const vis = { ...dpVisibility };
			vis[metricId] = 'show';
			setDPVisibility(vis);
		})
			.catch((e) => {
				setLoaderDisplay(false);
			});
	};

	const applyFilter = (f) => {
		setFilter(f);
		sortOrder = f.businessUnitIds;
		getDataViewData(f);
	};

	const resetData = () => {
		sortOrder = [];
		setDataViewData([]);
		setDataPoints({});
		setYearData([])
		setTopics([]);
	}

	const showOrHideTopic = (topicId) => {
		const vis = { ...topicVisibility };
		vis[topicId] = vis[topicId] === 'hide' ? 'show' : 'hide';
		setTopicVisibility(vis);
	};

	const showOrHideDataPoints = (metricId) => {
		const vis = { ...dpVisibility };

		vis[metricId] = vis[metricId] === 'show' ? 'hide' : 'show';
		setDPVisibility(vis);
		if (vis[metricId] === 'hide') {
			const existingDPs = JSON.parse(JSON.stringify(dataPoints));
			existingDPs[metricId] = [];
			setDataPoints(existingDPs);
			return;
		}
		getDataPoints(metricId);
	};

	const processFilteredData = (data) => {
		let yearData = [];
		let topics = [];
		let branches = [];
		let branchesConsolidated2ndYear = [];
		let branchesConsolidated3rdYear = [];
		let count = 0;

		/** When emission-linked, DataViewNew shows tCO2e; normalize `value` so rendering stays a single path. */
		const applyEmissionLinkedBranchValues = (metric) => {
			if (!metric.emissionLinked || metric.branchConsolidated == null) return;
			metric.branchConsolidated.forEach((row) => {
				if (row.branchId != null) {
					row.value = row.emissionSolutionInTCO2e;
				}
			});
		};

		if (data) {
			for (const [parentKey, patentValue] of Object.entries(data)) {
				if (patentValue) {
					for (const [childKey, childValue] of Object.entries(patentValue)) {
						yearData.push(parentKey + (childKey != 'YEARLY' ? '-' + childKey : ''));
						if (childValue.length > 0) {
							if (count == 0) {
								childValue.forEach(el => {
									el['metricConsolidated'].forEach(br => {
										if (br['branchConsolidated'] != null) {
											br['branchConsolidated'].sort((a, b) => {
												return sortOrder.indexOf(a.branchId) - sortOrder.indexOf(b.branchId);
											});
											applyEmissionLinkedBranchValues(br);
											const totalVal = br.emissionLinked ? br.emissionSolutionInTCO2e : br.total;
											br['branchConsolidated'].unshift({
												branchName: t("Total.label"),
												value: totalVal,
												uom: br.uom
											});
										} else {
											const totalVal = br.emissionLinked ? br.emissionSolutionInTCO2e : br.total;
											br['branchConsolidated'] = [{
												branchName: t("Total.label"),
												value: totalVal,
												uom: br.uom
											}];
										}
									})
								});
								topics = childValue;
								branches = childValue[0].metricConsolidated[0].branchConsolidated ?? [];
							}
							if (count > 0) {
								childValue.forEach((element, key1) => {
									element['metricConsolidated'].forEach((branch, key2) => {
										if (branch['branchConsolidated'] != null) {
											branch['branchConsolidated'].sort((a, b) => {
												return sortOrder.indexOf(a.branchId) - sortOrder.indexOf(b.branchId);
											});
											applyEmissionLinkedBranchValues(branch);
											const totalVal = branch.emissionLinked ? branch.emissionSolutionInTCO2e : branch.total;
											branch['branchConsolidated'].unshift({
												branchName: t("Total.label"),
												value: totalVal,
												uom: branch.uom
											});
											let exBrData = topics[key1]['metricConsolidated'][key2]['branchConsolidated'];
											let newBrData = branch['branchConsolidated'];
											let mergedData = exBrData.concat(newBrData)
											topics[key1]['metricConsolidated'][key2]['branchConsolidated'] = mergedData;
										} else {
											const totalVal = branch.emissionLinked ? branch.emissionSolutionInTCO2e : branch.total;
											branch['branchConsolidated'] = [{
												branchName: t("Total.label"),
												value: totalVal,
												uom: branch.uom
											}];
											let exBrData = topics[key1]['metricConsolidated'][key2]['branchConsolidated'];
											let newBrData = branch['branchConsolidated'];
											let mergedData = exBrData.concat(newBrData)
											topics[key1]['metricConsolidated'][key2]['branchConsolidated'] = mergedData;
										}
									});
								});
							}
							count++
						}
					}
				}
			}
			// console.log(branchesConsolidated2ndYear);
			// console.log(branchesConsolidated3rdYear);
			// topics.forEach(element => {
			// 	element['metricConsolidated'].forEach((data, key) => {
			// 		if (branchesConsolidated2ndYear.length > 0) {
			// 			data['branchConsolidated'] = data['branchConsolidated'].concat(branchesConsolidated2ndYear[key]);
			// 		}
			// 		if (branchesConsolidated3rdYear.length > 0) {
			// 			data['branchConsolidated'] = data['branchConsolidated'].concat(branchesConsolidated3rdYear[key]);
			// 		}
			// 	});
			// });
			setYearData(yearData)
			setBranches(branches);
			setTopics(topics);

		}
	};

	const renderTableHeader = () => {
		return (
			<thead>
				<tr>
					<th className="" rowSpan={2}>{t("Topics.label")}</th>
					{colHideShow.codes && <th className="text-center" rowSpan={2}>Code</th>}
					{colHideShow.uom && <th className="text-center" rowSpan={2}>UOM</th>}
					{
					yearData?.map((year, index) => {
						const displayYear = isFinancialYear && !year.includes('-') ? getFYLabel(year) : year;
						return (
							<th key={`branchHeader-${index}`} className="branch-cell text-center" colSpan={`${branches.length}`}>
								<span className="mx-1">{displayYear}</span>
							</th>
						);
					})
					}
				</tr>
				<tr className="compareBranches">
					{
						yearData.map(element => {
							return (
								<>
									{
										branches.map(branch => {
											return (
												<th className={`branch-cell ${filter.parentBranchIds?.indexOf(branch.branchId) >= 0 ? 'text-center' : 'child-branch-cell text-center'}`}>{branch.branchName}</th>
											)
										})
									}
								</>
							)
						})
					}
				</tr>
			</thead >
		);
	};

	const renderEmptyDataLabel = () => {
		return (
			<tr>
				<td colSpan={colHideShow ? 3 : 1} className="text-center 1">
					<div className="Nodatafound_box">
						<h6><FcFinePrint className="me-2" />{t("Nodatafound.label")}</h6>
					</div>
				</td>
			</tr>
		)
	};

	const renderTopicRow = (topic, index) => {
		const colSpanValue = (yearData?.length && branches?.length) ? (yearData.length * branches.length) : 2;
		return (
			<tr key={`topic-${topic.topicId}-${index}`} className="topic-row">
				<td colSpan={colSpanValue} className="name-cell">
					<span className="DataTopic_sticky cursor" onClick={() => {
						showOrHideTopic(topic.topicId)
					}}>
						<span className="left_arrow_head " >
							{(topicVisibility[topic.topicId] === 'hide') ? <FaChevronDown /> : <FaChevronUp />}
						</span>
						<span>{topic.topicName}</span>
					</span>
				</td>
			</tr>
		);
	};

	const renderMetricRow = (metric, index, topicId) => {
		return (
			<tr key={`metric-${metric.metricId}-${index}`}
				className={topicVisibility[topicId] === 'hide' ? 'metric-row d-none' : 'metric-row'}>
				<td className="name-cell">
					<div className="cursor" onClick={() => showOrHideDataPoints(metric.metricId)}>
						{
							metric.dataPointCount > 0 &&
							<span className="cursor left_arrow " >
								{dpVisibility[metric.metricId] === 'show' ? <FaChevronUp /> : <FaChevronDown />}
							</span>
						}
						<span>{metric.metricName}</span>
					</div>
				</td>
				{colHideShow.codes && <td className="text-center white-space">{metric.metricCode}</td>}
				{colHideShow.uom && <td className="text-center white-space">{metric.uom}</td>}
				{
					metric.branchConsolidated?.map((branch, bIndex) => {
						return (
							<td key={`metric-branch-cell-${bIndex}`}
								className={`branch-cell ${filter.parentBranchIds?.includes(branch.branchId) ? 'parent-branch-cell' : 'child-branch-cell'}`}>
								{branch.value != null ? Utils.formattedNumber(branch.value) : '-'}
							</td>
						);
					})
				}
			</tr>
		);
	};

	const renderDataPointRows = (metricId) => {
		if (!dataPoints[metricId]) return <></>;

		return (
			dataPoints[metricId]?.map((dataPoint, dIndex) => {
				return (
					<tr key={`dp-${dataPoint.dataPointId}-${dIndex}`} className="dp-row">
						<td className="name-cell">
							<span>{dataPoint.dataPointName}</span>
						</td>
						{colHideShow.codes && <td className="text-center">
							<span>{dataPoint.dataPointCode}</span>
						</td>}
						{colHideShow.uom && <td className="text-center">
							<span>{dataPoint.uom}</span>
						</td>}
						{
							dataPoint.branchConsolidated?.map((branch, bIndex) => {
								return renderDataPointBranchCell(branch, bIndex);
							})
						}
					</tr>
				)
			})
		);
	};

	const renderDataPointBranchCell = (branch, index) => {
		return (
			<>
				<td key={`dataPoint-branch-cell-${index}`}
					className={`branch-cell ${filter.parentBranchIds?.includes(branch.branchId) ? 'parent-branch-cell' : 'child-branch-cell'}`}>
					<span className='text-muted'>{branch.value != null ? Utils.formattedNumber(branch.value) : '-'}</span>
				</td>
			</>
		);
	};

	const renderCategoryRow = () => {
		const categoryColSpanValue = yearData?.length ? (yearData.length + 3) : 3;
		return (
			<tr key={`category`} className="cat-row">
				<td colSpan={categoryColSpanValue} className="name-cell 1">
					<span className="DataCat_sticky">
						<span className="text-primary">
							<b>{filter.category ? Utils.toTitleCase(t(filter.category)) : ''}</b>
						</span>
					</span>
				</td>
			</tr>
		);
	};

	const renderDataViewTable = () => {
		return (
			<div id='dataViewTableHolder 1' >
				<div className="table-holder table-responsive">
					<Table responsive="sm" bordered className="Compare_Data_table">
						{renderTableHeader()}
						<tbody>
							{renderCategoryRow()}
							{dataViewData?.length === 0 && renderEmptyDataLabel()}
							{
								topics?.length > 0 &&
								topics?.map((topic, tIndex) => {
									return (
										<>
											{renderTopicRow(topic, tIndex)}
											{
												topic.metricConsolidated?.length > 0 &&
												topic.metricConsolidated?.map((metric, mIndex) => {
													return (
														<>
															{renderMetricRow(metric, mIndex, topic.topicId)}
															{renderDataPointRows(metric.metricId)}
														</>
													);
												})
											}
										</>
									);
								})
							}
						</tbody>
					</Table>
				</div>
			</div>
		);
	};

	return (
		<div id='dataViewHolder'>
			<div className="List_header mb-1">
				<div><h5 className="MH">{t("Compare.label")}</h5></div>
				<CompareHeader
					filter={filter}
					regulationId={glbRegulationId}
					onFilterClick={() => setShowFilter(!showFilter)}
					onColHideShow={(val) => {
						const values = {
							...colHideShow,
							codes: val.codes,
							uom: val.uom
						}
						setColHideShow(values);
					}}
				/>
			</div>
			<div className={showFilter ? '' : 'd-none'}>
				<CompareFilter onApplyFilter={(f) => applyFilter(f)} onResetFilter={() => resetData()} onFilterHide={(value) => setShowFilter(value)} />
			</div>
			{renderDataViewTable()}
		</div>
	);
};

export default withTranslation()(CompareDataView);
